import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logError } from 'utils/errorHandler';
import { Button, StandalonePage } from 'design/ui';
import { textSize } from 'design/tokens';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component to catch React errors
 * Prevents white screen of death and provides user-friendly error display
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details using centralized error handler
    logError(error, 'ErrorBoundary');
    if (process.env.NODE_ENV === 'development') {
      // Only log errorInfo in development for debugging
      console.error('ErrorBoundary errorInfo:', errorInfo);
    }
    
    // Store error info for display
    this.setState({ errorInfo });
    
    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // TODO: Send to error tracking service (Sentry, etc.)
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, { contexts: { react: errorInfo } });
    // }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <StandalonePage title="Something went wrong">
          <p style={{ margin: 0 }}>
            {this.state.error?.message || 'The page hit an error it could not recover from. Reloading usually fixes it.'}
          </p>
          {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
            <details style={{ fontSize: textSize.small}}>
              <summary style={{ cursor: 'pointer' }}>Stack trace (development only)</summary>
              <pre style={{ overflow: 'auto', whiteSpace: 'pre-wrap', color: 'var(--st-neg)' }}>
                {this.state.error?.stack}
                {'\n\n'}
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="primary" onClick={this.handleReload}>
              Reload the page
            </Button>
            <Button variant="secondary" onClick={this.handleReset}>
              Try again
            </Button>
          </div>
        </StandalonePage>
      );
    }

    return this.props.children;
  }
}

